import InfoBlock, {
  InfoBlockAppearance,
  InfoBlockDefinition
} from './InfoBlock'
import { classNames } from '@/util/tailwindHelpers'

type Props = {
  definitions: InfoBlockDefinition[]
  blocksPerRow?: number
  orphansOnTop?: boolean
  className?: string
  blockAppearance?: InfoBlockAppearance
}

const InfoBlocks = ({
  definitions,
  blocksPerRow = 2,
  orphansOnTop,
  className,
  blockAppearance
}: Props): JSX.Element => {
  let groups = group(definitions, blocksPerRow, orphansOnTop)

  if (groups.length <= 0) return

  return (
    <div className={classNames('flex flex-col flex-1 gap-2', className)}>
      {groups.map((group, rowIndex) => {
        return (
          <div className="flex flex-1 gap-2" key={`infoblock-row-${rowIndex}`}>
            {group.map(({ appearance, ...props }, columnIndex) => {
              return (
                <InfoBlock
                  {...props}
                  style={group.length > 1 ? 'grouped' : 'solo'}
                  key={`infoblock-row-${rowIndex}-col-${columnIndex}`}
                  // prefers the indivual appearance over the group if it exists
                  appearance={appearance ?? blockAppearance}
                />
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

const group = <T,>(
  items: T[],
  itemsPerGroup: number,
  orphansOnTop: boolean
): T[][] => {
  const orphans = items.length % itemsPerGroup

  const groups = items.reduce(
    (acc, item, index) => {
      acc.currentRow.push(item)

      // if orphans should start the layout and on the first row
      if (
        orphansOnTop &&
        index < orphans &&
        acc.currentRow.length === orphans
      ) {
        acc.result.push(acc.currentRow)
        acc.currentRow = []

        if (index === orphans) {
          return acc
        }
      }

      if (
        acc.currentRow.length === itemsPerGroup ||
        index === items.length - 1
      ) {
        acc.result.push(acc.currentRow)
        acc.currentRow = []
      }
      return acc
    },
    {
      currentRow: [],
      result: []
    }
  )

  return groups.result
}

export default InfoBlocks
